import React, { useState, useEffect } from "react";
import { Modal, Button, Form, ProgressBar, Card } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import bulkUploadService from "../../services/bulkUploadService";
import Papa from "papaparse";
import { FaCloudUploadAlt, FaFileCsv, FaCheckCircle, FaTimesCircle, FaDownload } from "react-icons/fa";

const BulkUploadModal = ({ show, onHide, categories, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Setup, 2: Uploading/Progress, 3: Result
  const [selectedCategory, setSelectedCategory] = useState("");
  const [file, setFile] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  
  // Progress states
  const [status, setStatus] = useState("PENDING");
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0 });
  
  useEffect(() => {
    if (!show) {
      // Reset state on close
      setStep(1);
      setSelectedCategory("");
      setFile(null);
      setUploadId(null);
      setStatus("PENDING");
      setStats({ total: 0, success: 0, failed: 0 });
    }
  }, [show]);

  // Polling effect
  useEffect(() => {
    let interval;
    if (step === 2 && uploadId && status !== "COMPLETED" && status !== "FAILED") {
      interval = setInterval(async () => {
        try {
          const res = await bulkUploadService.getUploadStatus(uploadId);
          if (res.data) {
            setStatus(res.data.status);
            setStats({
              total: res.data.total_records,
              success: res.data.success_count,
              failed: res.data.failed_count
            });
            if (res.data.status === "COMPLETED" || res.data.status === "FAILED") {
              clearInterval(interval);
              setStep(3);
              if (res.data.status === "COMPLETED") {
                toast.success("Bulk upload processing finished!");
                if (onSuccess) onSuccess();
              } else {
                toast.error("Bulk upload processing failed.");
              }
            }
          }
        } catch (error) {
          console.error("Error polling status", error);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [step, uploadId, status, onSuccess]);

  const onDrop = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      setFile(uploadedFile);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024, // 20MB
    onDropRejected: (rejections) => {
      const error = rejections[0].errors[0];
      if (error.code === 'file-invalid-type') {
        toast.error("File upload failed: .csv file is required");
      } else {
        toast.error(error.message);
      }
    }
  });

  const handleDownloadSample = async () => {
    if (!selectedCategory) {
      toast.warning("Please select a category first.");
      return;
    }
    try {
      const res = await bulkUploadService.getSampleCsv(selectedCategory);
      if (res.data) {
        const headers = res.data.headers;
        // Create 1-2 dummy rows based on headers
        const dummyRow = {};
        headers.forEach(h => dummyRow[h] = "");
        if (headers.includes("product_name")) dummyRow["product_name"] = "Sample Book";
        if (headers.includes("selling_price")) dummyRow["selling_price"] = "100";
        if (headers.includes("mrp")) dummyRow["mrp"] = "150";

        const csv = Papa.unparse([dummyRow]);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `${res.data.category_name.replace(/\s+/g, '_')}_Sample.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      toast.error("Failed to generate sample CSV.");
    }
  };

  const handleUpload = async () => {
    if (!selectedCategory || !file) {
      toast.error("Category and File are required.");
      return;
    }
    try {
      setStep(2);
      const res = await bulkUploadService.processUpload(selectedCategory, file);
      if (res.data) {
        setUploadId(res.data.upload_id);
        setStats({ total: res.data.total_records, success: 0, failed: 0 });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Upload failed");
      setStep(1);
    }
  };

  const handleDownloadResult = async (type) => {
    try {
      const res = await bulkUploadService.getUploadResultCsvData(uploadId, type);
      if (res.data && res.data.length > 0) {
        const csv = Papa.unparse(res.data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `BulkUpload_${type}_${uploadId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.info(`No ${type} records found.`);
      }
    } catch (error) {
      toast.error(`Failed to download ${type} records.`);
    }
  };

  return (
    <Modal show={show} onHide={status === "PROCESSING" ? undefined : onHide} centered size="lg" backdrop="static" className="bulk-upload-modal">
      <Modal.Header closeButton={status !== "PROCESSING"} className="bg-dark text-white border-0 py-3">
        <Modal.Title className="fw-bold d-flex align-items-center">
          <FaCloudUploadAlt className="me-3 text-info" />
          Bulk Product Upload
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light px-4 py-4">
        {step === 1 && (
          <div className="animate-fade-in">
            {/* Step 1: Configuration */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
              <div className="card-header bg-white py-3 border-bottom-0">
                <h6 className="mb-0 fw-bold text-secondary">Step 1: Setup Your Import</h6>
              </div>
              <div className="card-body p-4">
                <div className="row g-3 align-items-end">
                  <div className="col-md-7">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted mb-1">1. Select Product Category</Form.Label>
                      <Form.Select 
                        value={selectedCategory} 
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="border-2 shadow-none rounded-3"
                        style={{ fontSize: '0.95rem', height: '42px' }}
                      >
                        <option value="">-- Choose Category --</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-md-5">
                    <Button 
                      variant="outline-dark" 
                      disabled={!selectedCategory}
                      onClick={handleDownloadSample}
                      className="w-100 rounded-3 fw-bold border-2 d-flex align-items-center justify-content-center"
                      style={{ fontSize: '0.9rem', height: '42px', transition: 'all 0.3s' }}
                    >
                      <FaDownload className="me-2" /> 
                      Download Sample
                    </Button>
                  </div>
                </div>
                {!selectedCategory && (
                  <div className="mt-2 small text-muted text-center" style={{ fontSize: '0.8rem' }}>
                    <i className="fa-solid fa-circle-info me-1"></i>
                    Select a category to enable template download
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: File Upload */}
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-header bg-white py-3 border-bottom-0">
                <h6 className="mb-0 fw-bold text-secondary">Step 2: Upload Files</h6>
              </div>
              <div className="card-body p-4">
                <div 
                  {...getRootProps()} 
                  className={`upload-zone border-2 rounded-4 p-5 text-center transition-all ${isDragActive ? 'border-primary bg-primary-soft shadow-sm' : 'border-dashed bg-white'}`}
                  style={{ 
                    cursor: 'pointer', 
                    borderStyle: 'dashed',
                    borderColor: isDragActive ? '#0d6efd' : '#dee2e6',
                    backgroundColor: isDragActive ? 'rgba(13, 110, 253, 0.05)' : 'white'
                  }}
                >
                  <input {...getInputProps()} />
                  {file ? (
                    <div className="animate-bounce-in">
                      <div className="icon-circle bg-success-soft text-success mx-auto mb-3" style={{ width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e1f7ec' }}>
                        <FaFileCsv size={40} />
                      </div>
                      <h5 className="fw-bold mb-1">{file.name}</h5>
                      <div className="text-muted small">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                      <Button variant="link" className="text-danger small p-0 mt-2" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                        Remove File
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="icon-circle bg-light text-muted mx-auto mb-3" style={{ width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                        <FaCloudUploadAlt size={40} />
                      </div>
                      <h5 className="fw-bold">Drag & drop your CSV file here</h5>
                      <p className="text-muted mb-0">or click to browse from computer</p>
                      <div className="mt-3">
                        <span className="badge bg-secondary-soft text-secondary px-3 py-2 rounded-pill me-2" style={{ backgroundColor: '#f0f0f0' }}>.CSV ONLY</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-5 text-center animate-fade-in">
            <div className="spinner-wrapper mb-4">
              <div className="spinner-border text-primary" style={{ width: '4rem', height: '4rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
            <h4 className="fw-bold mb-2">Processing Data</h4>
            <p className="text-muted mb-4">Parsing rows and validating master data records...</p>
            
            <div className="progress-container mx-auto mb-4" style={{ maxWidth: '400px' }}>
              <ProgressBar animated now={(stats.success + stats.failed) / stats.total * 100} className="rounded-pill" style={{ height: '12px' }} />
              <div className="d-flex justify-content-between text-muted small mt-2 fw-bold">
                <span>{stats.success + stats.failed} Processed</span>
                <span>{stats.total} Total</span>
              </div>
            </div>
            <div className="alert alert-info border-0 shadow-sm rounded-3 mx-auto" style={{ maxWidth: '400px' }}>
              <small className="fw-bold">Please stay on this page until completion.</small>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="p-2 animate-fade-in">
            <div className="text-center mb-4">
              <div className="icon-circle bg-success-soft text-success mx-auto mb-3 shadow-sm" style={{ width: '70px', height: '70px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e1f7ec' }}>
                <FaCheckCircle size={35} />
              </div>
              <h3 className="fw-bold">Import Finished!</h3>
              <p className="text-muted">Process for <strong>{stats.total}</strong> records has completed.</p>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm rounded-4 text-center overflow-hidden h-100">
                  <div className="card-body p-4 border-top border-4 border-success">
                    <h6 className="text-uppercase text-muted small fw-bold mb-3">Successfully Imported</h6>
                    <h1 className="display-4 fw-bold text-success mb-3">{stats.success}</h1>
                    <Button 
                      variant="success" 
                      className="rounded-pill px-4 fw-bold w-100"
                      onClick={() => handleDownloadResult('success')}
                      disabled={stats.success === 0}
                    >
                      <FaDownload className="me-2" /> Success Report
                    </Button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm rounded-4 text-center overflow-hidden h-100">
                  <div className="card-body p-4 border-top border-4 border-danger">
                    <h6 className="text-uppercase text-muted small fw-bold mb-3">Failed / Invalid</h6>
                    <h1 className="display-4 fw-bold text-danger mb-3">{stats.failed}</h1>
                    <Button 
                      variant="danger" 
                      className="rounded-pill px-4 fw-bold w-100"
                      onClick={() => handleDownloadResult('failed')}
                      disabled={stats.failed === 0}
                    >
                      <FaDownload className="me-2" /> Error Report
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-white border-0 py-3 px-4 shadow-sm">
        {step === 1 && (
          <>
            <Button variant="link" className="text-muted fw-bold text-decoration-none" onClick={onHide}>Cancel</Button>
            <Button variant="dark" className="px-5 rounded-pill fw-bold shadow-sm" onClick={handleUpload} disabled={!selectedCategory || !file}>
              Continue to Upload
            </Button>
          </>
        )}
        {step === 3 && (
          <Button variant="dark" className="px-5 rounded-pill fw-bold" onClick={onHide}>
            Done & Return
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUploadModal;
